// Libraries
import { Fragment, useCallback } from 'react';
import { QuestionOutlineIcon, TriangleDownIcon, TriangleUpIcon } from '@chakra-ui/icons';
import { Text, Flex, useBreakpointValue, Tooltip } from '@chakra-ui/react';
import { SortingColumn } from '@tanstack/react-table';
// Constants
import { SortOptions } from '@constants/index';
// Types
import { HeaderCellProps } from './HeaderTableCell.types';

/**
 * HeaderTableCell component represents a header cell within a table.
 *
 * @component
 * @template TableBody - The type representing the table body data.
 * @param {HeaderCellProps<TableBody>} props - The props for the HeaderTableCell component.
 * @param {Partial<Record<BreakpointsKeys, string>>} labels - Labels to be displayed in the header cell based on different breakpoints, including the base breakpoint..
 * @param {string} description - The additional description displayed in the header cell.
 * @param {SortingColumn<TableBody>} column - The sorting column associated with the header cell.
 * @param {boolean} [isWithSorting=true] - Determines whether to display sorting icons.
 * @returns {ReactElement} The rendered HeaderTableCell component.
 */
export const HeaderTableCell = <TableBody,>({
  description,
  column,
  isWithSorting = true,
  justifyContent = 'space-between',
  labels,
  tooltip,
}: HeaderCellProps<TableBody>) => {
  /**
   * Determines the color of the triangle icon based on the sorting column and sort option.
   *
   * @param {SortingColumn<TableBody>} column - The sorting column.
   * @param {SortOptions} sortOption - The sort option ('asc' or 'desc').
   * @returns {string} The color of the triangle icon ('base' or 'table.faded').
   */
  const triangleColor = useCallback(
    (column: SortingColumn<TableBody>, sortOption: SortOptions) =>
      column.getIsSorted() === sortOption ? 'base' : 'table.faded',
    []
  );

  const label = useBreakpointValue(labels, { ssr: false });

  return (
    <Flex
      py={1}
      px={3}
      borderBottom='section'
      minH={18}
      cursor='pointer'
      userSelect='none'
      flexDirection='column'
      justifyContent='space-between'
    >
      <Flex justifyContent={justifyContent} alignItems='flex-start'>
        {typeof label === 'string' ? (
          <Flex align='center' gap={1}>
            <Text fontSize='xs' variant='faded' fontWeight='medium' lineHeight={2} textTransform='uppercase'>
              {label}
            </Text>
          </Flex>
        ) : (
          <>{label}</>
        )}
        <Flex columnGap={1} alignItems='center' padding='1px 0 0 0'>
          {!!tooltip && (
            <Tooltip
              hasArrow
              w={200}
              placement='top'
              openDelay={100}
              closeDelay={200}
              gutter={12}
              bg='white'
              label={
                <Fragment>
                  <Text>{tooltip.title}</Text>
                  <Text variant='faded' noOfLines={2}>
                    {tooltip.description}
                  </Text>
                </Fragment>
              }
            >
              <QuestionOutlineIcon
                transition='all 0.2s ease-in-out'
                color='grey.100'
                boxSize={2.5}
                _hover={{
                  color: 'base',
                }}
              />
            </Tooltip>
          )}
          {column.getCanSort() && isWithSorting ? (
            <Flex direction='column'>
              <TriangleUpIcon boxSize={2} color={triangleColor(column, SortOptions.ASCENDANT)} />
              <TriangleDownIcon boxSize={2} color={triangleColor(column, SortOptions.DESCENDANT)} />
            </Flex>
          ) : null}
        </Flex>
      </Flex>
      {!!description && typeof description !== 'object' && (
        <Text fontSize='xs' variant='faded' lineHeight={2}>
          {description}
        </Text>
      )}
      {!!description && typeof description === 'object' && description}
    </Flex>
  );
};
